import { JmfPermissionEntity, PermissionNameType, PermissionOperationType } from "@mm-products/java-multima-framework";
import { SUPER_ADMIN_ROLE } from "./usePermissions";

export enum TaxPermissionEntity {
    TAX_CLIENT = "TaxClient",
    TAX_DOCUMENT = "TaxDocument",
    TAX_ACCOUNTANT = "TaxAccountant",
    TAX_LICENCE = "TaxLicence",
    TAX_OFFICER = "TaxOfficer",
    TAX_APPROVER = "TaxApprover",
    TAX_VAT_RATE = "TaxVatRate",
    TAX_DOCUMENT_ATTACHMENT = "TaxDocumentAttachment",
    TAX_PREACCOUNTING = "TaxPreAccounting",
    TAX_CENTRE = "TaxCentre",
    TAX_CONTRACT = "TaxContract",
    TAX_ACTIVITY = "TaxActivity",
    TAX_DOCUMENT_ITEM = "TaxDocumentItem",
    TAX_DOCUMENT_CENTRE_SHARE = "TaxDocumentCentreShare",
    TAX_SYSTEM_INTEGRATION = "TaxSystemIntegration",
}

export type EntityPermissionTypes = TaxPermissionEntity | JmfPermissionEntity | string;

class TaxPermissionDefinition {
    private readonly suffix: PermissionOperationType;
    private readonly entities: Array<EntityPermissionTypes>;

    constructor(suffix: PermissionOperationType, entities: Array<EntityPermissionTypes>) {
        this.suffix = suffix;
        this.entities = entities;
    }

    genPermissions(): Array<PermissionNameType> {
        return this.entities.map((entity) => `${entity.toString()}_${this.suffix}`);
    }
}

export class TaxPermissionBuilder {
    private builders: Array<TaxPermissionDefinition>;

    private constructor() {
        this.builders = [];
    }

    public static builder() {
        return new TaxPermissionBuilder();
    }

    public build(): Array<PermissionNameType> {
        const permissions = new Set<PermissionNameType>();
        this.builders.forEach((builder) => {
            builder.genPermissions().forEach((p) => permissions.add(p));
        });
        return Array.from(permissions);
    }

    public update(...entities: Array<EntityPermissionTypes>): TaxPermissionBuilder {
        this.builders.push(new TaxPermissionDefinition("update", entities));
        return this;
    }

    public read(...entities: Array<EntityPermissionTypes>): TaxPermissionBuilder {
        this.builders.push(new TaxPermissionDefinition("read", entities));
        return this;
    }

    public delete(...entities: Array<EntityPermissionTypes>): TaxPermissionBuilder {
        this.builders.push(new TaxPermissionDefinition("delete", entities));
        return this;
    }

    public create(...entities: Array<EntityPermissionTypes>): TaxPermissionBuilder {
        this.builders.push(new TaxPermissionDefinition("create", entities));
        return this;
    }

    public menu(...entities: Array<EntityPermissionTypes>): TaxPermissionBuilder {
        this.builders.push(new TaxPermissionDefinition("menu", entities));
        return this;
    }

    public custom(permission: PermissionOperationType, ...entities: Array<EntityPermissionTypes>) {
        this.builders.push(new TaxPermissionDefinition(permission, entities));
        return this;
    }
}

export const settingsSectionPermissions = TaxPermissionBuilder.builder()
    .menu(
        SUPER_ADMIN_ROLE,
        JmfPermissionEntity.CORE_USER,
        JmfPermissionEntity.CORE_ROLE,
        JmfPermissionEntity.CORE_SCHEDULER,
        JmfPermissionEntity.CORE_TASK_QUEUE,
        JmfPermissionEntity.CORE_ATTACHMENT,
        JmfPermissionEntity.CORE_PERSON,
        JmfPermissionEntity.CORE_COMPANY,
        TaxPermissionEntity.TAX_PREACCOUNTING
    )
    .build();
